import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { MageXPatient } from "@prisma/client";
import { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { JsonArray, JsonObject } from "@prisma/client/runtime/library";
import JSON5 from "json5";
import { Trans, useLingui } from "@lingui/macro";
import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import SelectCustom from "./SelectCustom";
import type { MultiValue } from "react-select";
import ScoreInput from "./ValueScoreInput";
import QuestionnaireScoreEditor from "./QuestionnaireScoreEditor";
import {
  correctedAnswersByDate,
  type Questionnaire,
  type StoredScore,
} from "@app/utils/questionnaires";
import { Textarea } from "./ui/textarea";
import CorrectionErrorClassPicker from "./CorrectionErrorClassPicker";
import TargetFieldPicker from "./TargetFieldPicker";
import RevisedCorrectionRecap from "./RevisedCorrectionRecap";
import { isSameFieldValue } from "@app/utils/resolve-field-value";
import type { ServedGesture } from "@app/utils/resolve-field-value";
import {
  asBoolean,
  correctionBody,
  openingForm,
  processFieldValue,
  readCorrectionForm,
  recordedValue,
  type CorrectionOpening,
  type RevisedCorrection,
} from "@app/utils/correction-gesture";
import {
  loggedRow,
  revisedCorrection,
  useCorrectionLog,
} from "@app/utils/correction-log";
import { valueLabel } from "@app/utils/value-labels";
import { asTerms } from "@app/utils/problem-list";
import type { FieldGesture } from "@api/src/types/patient";
import type {
  CorrectionContext,
  CorrectionContextResponse,
  CorrectionErrorClass,
} from "@app/types/correction";

interface ValueEditModalProps {
  loadPatient?: () => void;
  label: string;
  property: keyof MageXPatient;
  type?: TypeEnum;
  open: boolean;
  setIsOpen: (open: boolean) => void;
  patient?: MageXPatientWithFeedbacks;
  debug?: boolean;
  schema: PatientSchema;
  aggregatedValue:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  correctedValue:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  setCorrectedValue: (
    value:
      | string
      | number
      | boolean
      | JsonArray
      | Date
      | JsonObject
      | null
      | undefined,
  ) => void;
  /** Optional: parent uses this to light up its green check immediately
   * after a successful save, instead of waiting for the next loadPatient. */
  /** Le geste effectivement enregistré, remonté pour que la pastille du champ
   *  n'attende pas le rechargement du dossier. */
  onGestureRecorded?: (gesture: FieldGesture) => void;
  isNotStatued: boolean;
  calledFrom: string;
  /** Date clinique (AAAA-MM-JJ) que la correction décrit — la position du curseur
   * temporel. Fournie par la page temporelle uniquement : le champ de date apparaît
   * alors dans la modale, pré-rempli, et la correction est datée côté serveur. Absente
   * ailleurs : la correction décrit l'état courant (comportement historique). */
  effectiveDate?: string | null;
  scopeDocId?: string | null;
  /** Le questionnaire dont ce champ est le total, quand il en est le total. Sa présence bascule
   * la correction du total vers les réponses : on corrige l'item faux, pas le chiffre qu'il
   * produit. Absent pour un score lu dans un courrier — il n'a pas de sous-scores. */
  questionnaire?: Questionnaire;
  /** The gesture the row shows, as the server serves it. A correction or a flag reopens as it was
   *  made — value, reason, text — when the dossier's correction log is provided. */
  gesture?: ServedGesture | null;
}

type Score = {
  score: number;
  date: string;
};

export default function ValueEditModal({
  loadPatient,
  label,
  property,
  type = "string",
  open = false,
  setIsOpen,
  patient,
  debug = false,
  schema,
  aggregatedValue,
  correctedValue,
  setCorrectedValue,
  onGestureRecorded,
  isNotStatued,
  calledFrom,
  effectiveDate = null,
  scopeDocId = null,
  questionnaire,
  gesture = null,
}: ValueEditModalProps) {
  const reader = useUser((state) => state.user);
  const correctionLog = useCorrectionLog();
  const { t } = useLingui();
  const [explanation, setExplanation] = useState("");
  const [saving, setSaving] = useState(false);
  // Le refus du serveur, rendu visible.
  //
  // Le POST peut être refusé — un 422 quand le serveur, qui recanonicalise les deux
  // valeurs, conclut au signalement là où l'écran croyait à une correction (une
  // modification réduite à des espaces, par exemple) et refuse alors une raison qui
  // suppose la bonne valeur. Jusqu'ici l'échec n'allait qu'à la console : le bouton
  // semblait ne rien faire, et le médecin recliquait. Le serveur écrit ses messages en
  // français, à destination de ce médecin ; il n'y a qu'à les afficher.
  const [saveError, setSaveError] = useState<string | null>(null);
  // Date clinique décrite par la correction. Modifiable : le curseur donne la valeur
  // par défaut, mais un clinicien peut vouloir dater sa correction d'un autre jour que
  // celui qu'il consulte.
  const [clinicalDate, setClinicalDate] = useState(effectiveDate ?? "");

  // ── Justification (PT-60) ────────────────────────────────────────────────────────
  // What the doctor used to write is now a closed choice the machine pre-fills. Prose is
  // required on the five classes whose structure does not say why, optional on the other
  // three.
  //
  // Le registre d'assertions n'est consulté que sur la surface doc-first. Sur la page
  // d'agrégation à plat il n'a pris aucune part à la production de la valeur corrigée :
  // y proposer ses passages inviterait à citer une preuve étrangère à la réponse qu'on
  // rectifie. ``effectiveDate`` est le discriminant existant — la prop n'est fournie que
  // par la page temporelle, comme sa documentation ci-dessus l'indique.
  const isDocFirstSurface = effectiveDate !== null;
  // Le journal de corrections n'existe que pour un dossier. Sans ``patient``, cette modale
  // n'est qu'un éditeur local — la table de références l'ouvre ainsi — et rien de ce qui
  // décrit une erreur n'y aurait de destinataire.
  const recordsFeedback = Boolean(patient?.id_magex);
  const [context, setContext] = useState<CorrectionContext | null>(null);
  // La valeur que ``context`` décrit — et pas une de plus.
  //
  // Le contexte est chargé deux fois : une fois à l'ouverture, SANS valeur proposée, puis
  // à chaque frappe après une temporisation de 500 ms. Le premier appel répond donc à une
  // question que le médecin n'a pas posée : le serveur y lit « vous videz ce champ », et
  // renvoie sur 291 dossiers de production sur 300 une restriction à trois classes — cinq
  // grisées — voire, sur 9 d'entre eux, une classe pré-remplie « ce champ aurait dû rester
  // vide ».
  //
  // Tant que ce repère ne vaut pas la valeur en cours, la suggestion décrit autre chose et
  // n'est pas lue. Sans lui, la première frappe faisait grisailler cinq choix pendant une
  // demi-seconde, et la classe pré-remplie était enregistrable telle quelle.
  const [contextFor, setContextFor] = useState<string | null>(null);
  // Ce que le MÉDECIN a cliqué, et rien d'autre.
  //
  // La proposition de la machine n'entre jamais dans cet état : elle est appliquée plus
  // bas, au moment de lire la classe effective, et seulement une fois la valeur modifiée.
  // Cette séparation est ce qui rend le geste déductible. Sans elle, une classe
  // pré-remplie à l'ouverture ferait d'un enregistrement sans modification un signalement,
  // alors que le médecin croyait confirmer la valeur affichée.
  const [errorClass, setErrorClass] = useState<CorrectionErrorClass | null>(
    null,
  );
  const [targetField, setTargetField] = useState<string | null>(null);
  // Une ref plutôt qu'une lecture d'état dans l'effet débouncé plus bas : celui-ci ÉCRIT
  // ``context``, donc le lire ferait de sa propre réponse une dépendance et relancerait la
  // requête en boucle.
  const contextLoadedRef = useRef(false);

  // La valeur en cours d'édition. Un BROUILLON local, et c'est la décision structurante de
  // cet écran.
  //
  // La saisie écrivait jusqu'ici directement dans l'état du parent, à chaque frappe. Fermer
  // par la croix, par Échap ou par un clic à côté laissait donc la valeur tapée affichée
  // sur la page — et un champ jamais statué y passait pour validé — jusqu'au prochain
  // rechargement. « Annuler » tentait de réparer cela en restaurant ``patientTruth``, qui
  // vaut null sur la page temporelle : le remède y effaçait la valeur affichée. Rien ne
  // sort plus d'ici avant un enregistrement réussi, donc aucune fermeture ne peut mentir.
  const [draft, setDraft] = useState<unknown>(undefined);

  // La valeur que le médecin AVAIT sous les yeux en ouvrant la modale. C'est elle qui
  // décide si l'enregistrement est une correction ou une confirmation, et c'est elle que
  // ``old_value`` doit porter : jusqu'ici le corps envoyait la colonne agrégée brute, si
  // bien qu'un médecin revenant sur sa propre correction déclarait partir d'une valeur
  // qu'il ne voyait plus.
  const [baselineValue, setBaselineValue] = useState<unknown>(undefined);
  // Le même repère, lisible depuis un callback stable — celui par lequel l'éditeur de
  // questionnaire remonte sa série recalculée.
  const baselineRef = useRef<unknown>(undefined);
  // The same value as the page showed it, kept when the editor reads it in other terms.
  const [shownValue, setShownValue] = useState<unknown>(undefined);

  // Fermeture demandée alors que le brouillon serait perdu : on le dit avant de fermer.
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Le total d'un questionnaire est recalculé par le serveur ; tant que le recalcul court,
  // le brouillon porte un chiffre périmé et l'enregistrement doit attendre.
  const [scoresComputing, setScoresComputing] = useState(false);
  // The correction this opening revises, and the one it shows — the same, or a superseded one
  // shown for what it said while the form starts blank (PT-165).
  const [revised, setRevised] = useState<RevisedCorrection | null>(null);
  const [pastCorrection, setPastCorrection] =
    useState<RevisedCorrection | null>(null);

  // Ouverture : formulaire vierge, brouillon posé sur la valeur affichée.
  //
  // Pendant le rendu et non dans un effet — un effet ne s'exécute qu'après la peinture, et
  // le premier rendu montrerait donc un champ vide le temps d'une image. C'est le motif
  // « ajuster l'état quand une prop change » de React, dont la condition garantit qu'il ne
  // s'exécute qu'une fois par ouverture.
  const [sessionOpen, setSessionOpen] = useState(false);
  if (open !== sessionOpen) {
    setSessionOpen(open);
    if (open) {
      // ``isNotStatued`` est lu ICI et jamais plus : il DÉRIVE de ``correctedValue`` côté
      // parent, donc le relire après coup ferait basculer le repère sous la frappe en
      // cours et une correction repartirait enregistrée comme une confirmation.
      // A reality is edited as its label: the list editors write terms.
      const shown = isNotStatued ? aggregatedValue : correctedValue;
      const initial = asTerms(shown);
      const shownNow = initial === shown ? undefined : shown;
      // The correction the row shows, read once per opening like the baseline: a correction is
      // reopened as it was made, a superseded one only shown.
      const row = recordsFeedback
        ? loggedRow(correctionLog, String(property), gesture)
        : null;
      const revisedNow = revisedCorrection(row, gesture, reader);
      setRevised(revisedNow);
      setPastCorrection(
        revisedNow ??
          revisedCorrection(
            row,
            gesture ? { ...gesture, superseded: false } : null,
            reader,
          ),
      );
      const form = openingForm({
        type,
        baselineValue: initial,
        shownValue: shownNow,
        recordsFeedback,
        effectiveDate,
        scopeDocId,
        revised: revisedNow,
      });
      setDraft(initial);
      setBaselineValue(initial);
      setShownValue(shownNow);
      baselineRef.current = initial;
      setClinicalDate(form.clinicalDate);
      setErrorClass(form.errorClass);
      setTargetField(form.targetField);
      setExplanation(form.explanation);
      setConfirmingClose(false);
      setScoresComputing(false);
      setSaveError(null);
    }
  }

  useEffect(() => {
    if (open && patient?.id_magex) {
      // Vider d'abord : un contexte resté d'une ouverture précédente décrirait un autre
      // champ, et sa suggestion serait lue comme celle de celui-ci.
      setContext(null);
      setContextFor(null);
      contextLoadedRef.current = false;
      API.get({
        path: `/correction/${patient.id_magex}/context/${property}`,
        query: { with_passages: isDocFirstSurface },
      }).then((res: CorrectionContextResponse) => {
        if (res.ok && res.data) {
          setContext(res.data);
          // Aucune valeur n'a été proposée à cet appel : sa suggestion ne décrit aucune
          // correction, et ``null`` ne peut égaler aucun brouillon sérialisé.
          setContextFor(null);
          contextLoadedRef.current = true;
        }
      });
    }
  }, [property, patient?.id_magex, open, isDocFirstSurface]);

  const valueInModal = draft;

  // La forme sous laquelle le brouillon part au serveur — et le repère qui dit si la
  // suggestion reçue décrit bien CETTE valeur. Une seule écriture pour les deux usages :
  // deux sérialisations qui divergeraient feraient croire la suggestion périmée en
  // permanence, ce qui la neutraliserait en silence.
  const serializedDraft = useMemo(
    () =>
      valueInModal === null || valueInModal === undefined
        ? ""
        : typeof valueInModal === "string"
          ? valueInModal
          : JSON.stringify(valueInModal),
    [valueInModal],
  );

  // La suggestion n'est lue que si elle décrit la valeur en cours. Voir ``contextFor`` :
  // l'appel d'ouverture répond sur une valeur que personne n'a proposée, et son verdict
  // resterait affiché pendant la temporisation qui suit la première frappe.
  const suggestionIsCurrent =
    contextFor !== null && contextFor === serializedDraft;
  const suggestedClass = suggestionIsCurrent
    ? (context?.suggestion.error_class ?? null)
    : null;
  // Le geste, déduit du formulaire et jamais du bouton : valeur inchangée sans raison, il
  // confirme ; avec une raison, il signale ; valeur modifiée, il corrige — et une correction
  // rouverte, valeur gardée, se corrige à nouveau (``utils/correction-gesture``).
  const opening: CorrectionOpening = {
    type,
    baselineValue,
    shownValue,
    recordsFeedback,
    effectiveDate,
    scopeDocId,
    revised,
  };
  const form = {
    draft: valueInModal,
    errorClass,
    explanation,
    targetField,
    clinicalDate,
  };
  const reading = readCorrectionForm(opening, form, suggestedClass);
  const {
    effectiveErrorClass,
    gestureToRecord,
    offersOptionalProse,
    showsProse,
    needsTargetField,
    reasonNeedsAValue,
    justificationBlocked,
    isDirty,
    nothingToRevise,
  } = reading;
  // Each example points at what that class's structure can miss.
  const optionalProsePlaceholder =
    effectiveErrorClass === "VOCABULARY"
      ? t`Ex. : le terme exact s'il n'est pas dans la liste, ou le synonyme employé dans le document.`
      : effectiveErrorClass === "WRONG_FIELD"
        ? t`Ex. : c'est l'intervention de cet épisode, pas un antécédent.`
        : t`Ex. : aucun document du dossier ne parle de tabagisme.`;

  /** Toute fermeture passe par ici — croix, Échap, clic à côté, bouton Annuler. Une seule
   *  règle, donc : aucune d'elles ne perd une saisie sans le dire. */
  const requestClose = () => {
    // Pendant l'enregistrement, aucune sortie : la requête en vol écrira la valeur et
    // rechargera le dossier, et fermer ici ferait croire au médecin qu'il l'a annulée.
    if (saving) return;
    if (isDirty) setConfirmingClose(true);
    else setIsOpen(false);
  };

  async function giveCorrectionFeedback() {
    if (justificationBlocked) return;
    setSaveError(null);
    // Le brouillon, et lui seul : tous les éditeurs de cette modale écrivent dedans, y
    // compris ceux des scores. Un chemin d'enregistrement unique est ce qui permet au
    // pied de modale d'être unique.
    let processedValue: unknown = valueInModal;
    setSaving(true);

    // Process the value based on type
    if (type === "number" || type === "date") {
      processedValue = processFieldValue(type, processedValue);
    } else if (type === "boolean") {
      processedValue = asBoolean(processedValue);
    } else if (
      type === "array_string" ||
      type === "array_enum" ||
      type === "scores"
    ) {
      try {
        // If it's already an array, return as is
        if (Array.isArray(processedValue)) {
          // processedValue = processedValue;
        } else {
          processedValue = JSON5.parse(processedValue as string);
        }
      } catch (error) {
        console.warn("Error parsing JSON:", error, processedValue);
        // processedValue = processedValue;
      }
    }

    if (patient) {
      // Une confirmation ne porte ni classe, ni prose, ni champ cible : ces colonnes décrivent
      // une erreur, et il n'y en a pas eu.
      const body = correctionBody(
        String(property),
        opening,
        form,
        reading,
        suggestedClass,
        processedValue,
      );

      if (debug)
        console.warn(`EDIT MODAL ${calledFrom}`, {
          body,
          type,
          "typeof aggregatedValue": typeof aggregatedValue,
          processedValue,
        });
      const response = await API.post({
        path: `/patient/${patient.id_magex}/feedback`,
        body,
      });

      if (response.ok) {
        // Le seul endroit d'où le brouillon sort. Tant qu'on n'est pas passé par ici, la
        // page n'a rien vu changer — ni à l'écran, ni au rechargement.
        setCorrectedValue(
          recordedValue(opening, reading, processedValue) as Parameters<
            typeof setCorrectedValue
          >[0],
        );
        // Le geste que la pastille affiche vient du SERVEUR, jamais de la lecture que
        // l'écran fait de sa propre soumission. Les deux peuvent diverger : le serveur
        // recanonicalise (espaces retirés, couches JSON pelées, ordre des listes
        // significatif) là où la modale compare structurellement. Une pastille tirée du
        // verdict local aurait alors affiché une croix jusqu'au rechargement, puis se
        // serait changée en tiret sous les yeux du médecin. ``gestureToRecord`` ne sert
        // plus que de repli, pour un serveur qui ne renverrait pas le sien.
        const recorded: FieldGesture =
          response?.gesture === "validated" ||
          response?.gesture === "corrected" ||
          response?.gesture === "flagged"
            ? response.gesture
            : gestureToRecord;
        onGestureRecorded?.(recorded);
        setIsOpen(false);
        loadPatient?.();
      } else {
        console.error("Feedback POST failed", response);
        // ``detail`` est le champ de FastAPI, et il porte la phrase que le serveur a
        // rédigée pour ce refus précis ; ``error`` est celui que la couche API pose sur
        // les pannes réseau. Le repli générique n'est atteint que si aucun des deux ne
        // dit rien d'utilisable.
        const detail =
          typeof response?.detail === "string" ? response.detail : null;
        const generic =
          typeof response?.error === "string" ? response.error : null;
        setSaveError(
          detail ??
            generic ??
            t`L'enregistrement a échoué. Réessayez dans un instant.`,
        );
      }
    } else {
      // Aucun dossier derrière le champ (table de références) : il n'existe pas de journal
      // où écrire, l'édition ne vaut que pour l'écran qui l'a ouverte.
      setCorrectedValue(
        recordedValue(opening, reading, processedValue) as Parameters<
          typeof setCorrectedValue
        >[0],
      );
      setIsOpen(false);
    }
    setSaving(false);
  }

  const fieldSchema = schema[property];
  if (!fieldSchema) {
    if (debug) console.warn("No field schema", property);
  }

  // La suggestion ne devient utile qu'une fois la vraie valeur connue : « la valeur
  // corrigée figure-t-elle dans un document » est la moitié du raisonnement, et elle
  // n'est calculable qu'ici. D'où ce rafraîchissement débouncé plutôt qu'un unique
  // appel à l'ouverture — un appel par frappe coûterait une requête par caractère.
  useEffect(() => {
    if (!open || !patient?.id_magex || !contextLoadedRef.current) {
      return;
    }
    const serialized = serializedDraft;
    const timer = setTimeout(() => {
      API.get({
        path: `/correction/${patient.id_magex}/context/${property}`,
        // Le drapeau doit être renvoyé : cette réponse REMPLACE le contexte, donc
        // l'omettre viderait la liste de passages que la modale vient d'afficher.
        query: { new_value: serialized, with_passages: isDocFirstSurface },
      }).then((res: CorrectionContextResponse) => {
        if (!res.ok || !res.data) return;
        // Seul le contexte est rafraîchi. La classe cochée par le médecin n'est jamais
        // touchée, et la proposition ne s'applique que là où elle est lue.
        setContext(res.data);
        setContextFor(serialized);
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [serializedDraft, open, patient?.id_magex, property, isDocFirstSurface]);

  const isCurrentlyUndetermined =
    valueInModal === null ||
    valueInModal === undefined ||
    valueInModal === "" ||
    (Array.isArray(valueInModal) && valueInModal.length === 0);
  const markAsUndetermined = () => setDraft(null);

  // Mémoïsé : cet objet est une dépendance de l'éditeur de questionnaire, qui en dérive la
  // série remontée ici. Le reconstruire à chaque rendu ferait recalculer cette série sans
  // qu'aucune réponse ait bougé.
  const correctedAnswers = useMemo(
    () => correctedAnswersByDate(correctedValue),
    [correctedValue],
  );
  /**
   * Every editor in this modal writes the value through here.
   *
   * The draft is authoritative: emptying a value — removing the last chip of a
   * multiselect, clearing the text input — now sticks on its own. The display used to fall
   * back to the AGGREGATED value as soon as the corrected one was empty, so a clinician
   * deleted the value the machine had found and watched it reappear, which read as
   * "impossible de supprimer, seulement d'ajouter". A local draft removes both the defect
   * and the ``isCleared`` flag that used to patch it.
   */
  const updateValue = (next: unknown) => setDraft(next);

  /**
   * L'éditeur de questionnaire ne saisit pas un total : il corrige des réponses et fait
   * recalculer la série par le serveur. Il la remonte ici, dans le même brouillon que
   * tous les autres éditeurs — c'est ce qui permet au pied de modale d'être unique.
   *
   * ``series === null`` signifie « aucune réponse touchée » : le brouillon revient alors
   * à la valeur d'ouverture, sans quoi une modification défaite resterait comptée comme
   * une correction.
   */
  const handleScoresDraft = useCallback(
    ({
      series,
      computing,
    }: {
      series: StoredScore[] | null;
      computing: boolean;
    }) => {
      setScoresComputing(computing);
      // Comparaison de CONTENU avant d'écrire, et non d'identité : l'éditeur reconstruit
      // sa série à chaque rendu, donc la poser telle quelle rendrait le brouillon
      // différent à chaque fois, ce qui relancerait un rendu, qui reconstruirait la
      // série — une boucle infinie dès la première réponse corrigée.
      const next = series ?? baselineRef.current;
      setDraft((current: unknown) =>
        isSameFieldValue(current, next) ? current : next,
      );
    },
    [],
  );
  let isMultiselect = false;
  if (fieldSchema?.type === "array_enum" && fieldSchema.items?.enum)
    isMultiselect = true;
  if (fieldSchema?.type === "array_string") isMultiselect = true;
  // Un champ déclaré scalaire dont la valeur EN PORTE PLUSIEURS s'édite comme la liste qu'il est.
  //
  // La page et cette modale ne lisaient pas la même chose : le rendu suit la valeur, la modale
  // suivait la déclaration. « Traitement médical » est déclaré `string` et le parcours de soins y
  // dépose une entrée par réalité établie au même moment — cinq médicaments sur un compte rendu
  // qui en nomme cinq. Le médecin lisait donc cinq lignes et se voyait offrir une seule case, où
  // la seule façon de tout garder était de les recoller à la virgule — ce qu'un dossier de
  // production porte effectivement dans son journal de corrections.
  //
  // Lu sur la valeur D'OUVERTURE et non sur le brouillon : sinon retirer l'avant-dernière pastille
  // changerait le widget sous la main du médecin, au milieu de sa saisie.
  //
  // Ne déclare rien : un champ qui porte durablement plusieurs valeurs reste une ligne d'ontologie
  // à retyper en `array_string`, et c'est elle qui ferait apparaître ce widget même sur un champ
  // encore vide.
  //
  // Borné au TEXTE, et le bornage n'est pas de la prudence : une série de scores est une liste
  // d'objets datés qui a son propre éditeur, un nombre a le sien, et ce widget rend chaque élément
  // comme son propre libellé. Il n'ouvre donc que là où la valeur est ce qu'il sait écrire.
  if (
    !isMultiselect &&
    (fieldSchema?.type ?? "string") === "string" &&
    Array.isArray(baselineValue) &&
    baselineValue.every((item) => typeof item === "string")
  )
    isMultiselect = true;
  if (debug)
    console.warn(`VALUE EDIT MODAL ${calledFrom}`, {
      fieldSchema,
      isMultiselect,
      valueInModal,
      isNotStatued,
    });
  let options: string[] = [];
  if (isMultiselect) {
    options = fieldSchema?.items?.enum?.length
      ? fieldSchema.items.enum
      : // Un champ déclaré scalaire édité comme liste garde SA liste fermée quand il en déclare
        // une : `items` n'existe que sur les types tableau, mais le vocabulaire d'un enum reste
        // le vocabulaire de chacune des valeurs.
        fieldSchema?.enum?.length
        ? fieldSchema.enum
        : [fieldSchema?.empty || ""];
    if (debug)
      console.warn(`VALUE EDIT MODAL ${calledFrom} MULTISELECT`, { options });
  }
  // A choice is a TERM — what is written, whatever language the reader reads — shown under its
  // label in that language. The label never leaves this screen.
  const labels = fieldSchema?.value_labels;
  const asOption = (term: string) => ({
    label: valueLabel(labels, term),
    value: term,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? setIsOpen(true) : requestClose())}
    >
      <DialogContent
        className={[
          type === "array_string" || type === "array_enum" || type === "scores"
            ? "max-w-4xl"
            : "max-w-lg",
        ].join(" ")}
      >
        <DialogHeader>
          <DialogTitle>
            <Trans>{label}</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 pb-4 max-h-[80vh] overflow-y-auto">
          <DialogDescription>
            <Trans>
              Donnez la vraie valeur pour la propriété "{label}" de ce patient.
            </Trans>
            {fieldSchema?.description && (
              <>
                <br />
                {fieldSchema?.description || ""}
              </>
            )}
          </DialogDescription>
          {pastCorrection && (
            <RevisedCorrectionRecap
              correction={pastCorrection}
              revising={revised !== null}
              property={property}
              type={type}
              schema={schema}
              // Revised, the value on screen is the one it gave; superseded, the one it judged.
              correctedValue={revised ? baselineValue : gesture?.value}
            />
          )}
          <div className="grid gap-2">
            <Label htmlFor={`${property}-input`}>
              <Trans>Vraie valeur</Trans>
            </Label>
            {isMultiselect ? (
              <div className="space-y-2 z-[999999] relative">
                <SelectCustom
                  options={options.map(asOption)}
                  value={(Array.isArray(valueInModal) ? valueInModal : []).map(
                    (term) => asOption(String(term)),
                  )}
                  getOptionLabel={(f) => f.label as string}
                  getOptionValue={(f) => f.value as string}
                  onChange={(newValue) => {
                    newValue = newValue as MultiValue<{
                      label: string;
                      value: string;
                    }>;
                    updateValue(newValue?.map((option) => option.value) || []);
                  }}
                  placeholder={
                    options.some((option) => option)
                      ? t`Sélectionnez une valeur`
                      : t`Entrez une nouvelle valeur`
                  }
                  isClearable
                  isMulti
                  // A declared list is the field's whole answer (« Autre » included): typing outside
                  // it created the categories the dashboard then counted apart.
                  creatable={!(fieldSchema?.items?.enum?.length || fieldSchema?.enum?.length)}
                  inputId={`${property}-input`}
                />
              </div>
            ) : fieldSchema?.enum ? (
              <Select
                value={valueInModal as string}
                onValueChange={(value) => updateValue(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Sélectionnez une valeur`} />
                </SelectTrigger>
                <SelectContent>
                  {fieldSchema.enum.map((option) => {
                    return (
                      <SelectItem key={option} value={option}>
                        {valueLabel(labels, option)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : fieldSchema?.type === "boolean" ? (
              <RadioGroup
                id={`${property}-input`}
                className="flex-row flex gap-x-6"
                value={
                  asBoolean(valueInModal) === true
                    ? "oui"
                    : asBoolean(valueInModal) === false
                      ? "non"
                      : ""
                }
                onValueChange={(v) =>
                  updateValue(v === "oui" ? true : v === "non" ? false : null)
                }
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="oui" id={`${property}-oui`} />
                  {t`Oui`}
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="non" id={`${property}-non`} />
                  {t`Non`}
                </label>
              </RadioGroup>
            ) : fieldSchema?.type === "number" ? (
              <Input
                type="number"
                value={(valueInModal as number) ?? ""}
                onChange={(e) => updateValue(e.target.value)}
                step={1}
              />
            ) : fieldSchema?.type === "scores" ? (
              <div className="space-y-2">
                {/* Deux surfaces pour un même champ, et c'est la donnée qui choisit : dès que le
                    score a un questionnaire derrière lui, on corrige les réponses. La saisie
                    date + total reste pour les scores lus dans un courrier, qui n'ont pas
                    d'items — et pour eux, saisir le total est la seule chose à faire. */}
                {questionnaire &&
                questionnaire.passations.length > 0 &&
                patient ? (
                  <QuestionnaireScoreEditor
                    questionnaire={questionnaire}
                    fieldKey={property as string}
                    tenantId={patient.tenant_id}
                    patientId={patient.id_patient}
                    onDraftChange={handleScoresDraft}
                    corrected={correctedAnswers}
                  />
                ) : (
                  <ScoreInput
                    value={valueInModal as unknown as Array<Score>}
                    property={property}
                    onChange={updateValue}
                  />
                )}
                {/* Add validation indicator */}
                {/* {typeof valueInModal === "string" && valueInModal && (
              <div className="text-sm">
                {try {
                  JSON5.parse(valueInModal);
                  return <span className="text-green-600">✓ JSON valide</span>;
                } catch {
                  return <span className="text-red-600">✗ JSON invalide</span>;
                }}
              </div>
            )} */}
              </div>
            ) : type === "date" ? (
              <Input
                type="date"
                value={(valueInModal as string) ?? ""}
                onChange={(e) => updateValue(e.target.value)}
              />
            ) : (
              <Input
                type="text"
                value={(valueInModal as string) ?? ""}
                onChange={(e) => updateValue(e.target.value)}
              />
            )}
            <Button
              variant="ghost"
              type="button"
              className="w-fit text-slate-500 hover:text-slate-700 px-0"
              onClick={markAsUndetermined}
              disabled={isCurrentlyUndetermined}
              title={t`Vide les inputs ; cliquez ensuite sur Enregistrer pour persister`}
            >
              <Trans>Marquer comme non déterminé</Trans>
            </Button>
          </div>
          {/* Page temporelle uniquement : une correction y décrit un jour précis, pas
              l'état courant. Sans cette date, valider depuis le curseur écrirait la
              valeur d'aujourd'hui et le passé du dossier deviendrait faux. */}
          {effectiveDate !== null && recordsFeedback && (
            <div className="grid gap-2 rounded-lg border border-primary/20 bg-blue-50 p-3">
              <Label
                htmlFor={`${property}-clinical-date`}
                className="text-blue-900"
              >
                <Trans>Cette valeur est vraie à partir du</Trans>
              </Label>
              <Input
                id={`${property}-clinical-date`}
                type="date"
                className="bg-white"
                value={clinicalDate}
                onChange={(e) => setClinicalDate(e.target.value)}
              />
              <p className="text-xs text-slate-600">
                <Trans>
                  La correction ne s'applique qu'à partir de ce jour : les dates
                  antérieures du dossier gardent leur valeur.
                </Trans>
              </p>
            </div>
          )}
          {/* Le bloc est VISIBLE et ACTIF dès l'ouverture.
              Le masquer privait le médecin de ce qu'on allait lui demander ; le griser
              tant que la valeur n'avait pas bougé revenait à dire qu'une raison n'a de
              sens qu'avec une correction — alors qu'une raison SANS correction est
              précisément le signalement, et qu'un bouton dédié y suppléait mal. */}
          {recordsFeedback && (
            <div className="grid gap-3 border-t pt-4">
              {reading.hints.optionalBlock && (
                <p className="text-xs text-slate-500">
                  <Trans>
                    Facultatif : laissez ce bloc vide pour confirmer la valeur
                    affichée. Donner une raison sans corriger la valeur
                    enregistre un signalement.
                  </Trans>
                </p>
              )}
              <CorrectionErrorClassPicker
                value={effectiveErrorClass}
                // Sur une valeur inchangée, recliquer la raison la retire — c'est le
                // chemin de retour vers la confirmation, et sans lui le médecin serait
                // enfermé dans un signalement par un clic d'essai.
                onChange={(next) =>
                  setErrorClass((current) =>
                    reading.reasonToggles && current === next ? null : next,
                  )
                }
                suggested={suggestedClass}
                // AUCUNE atténuation par défaut. Les huit choix sont pleins tant qu'une
                // trouvaille positive, portant sur la valeur réellement saisie, n'en a pas
                // éliminé.
                //
                // Deux sources de grisage ont été retirées ici. La première venait du
                // serveur et tombait sur 94,7 % des corrections réelles ; elle est traitée
                // dans ``correction_classes.suggest_error_class``. La seconde était locale :
                // tant que la valeur n'avait pas bougé, on grisait les quatre classes qui
                // supposent la bonne valeur. La contrainte est réelle — le serveur refuse
                // un signalement portant l'une des autres — mais elle n'a pas besoin d'être
                // dite en éteignant la moitié de la liste avant que le médecin ait rien
                // fait. Elle est dite quand elle s'applique, par ``reasonNeedsAValue``, qui
                // nomme la sortie au lieu de la suggérer par une couleur.
                candidates={
                  suggestionIsCurrent
                    ? context?.suggestion.candidate_classes
                    : []
                }
                rationale={
                  suggestionIsCurrent
                    ? context?.suggestion.rationale
                    : undefined
                }
              />

              {reasonNeedsAValue && (
                <p className="text-xs text-red-600">
                  <Trans>
                    Cette raison suppose que vous donniez la bonne valeur :
                    corrigez la valeur ci-dessus, ou choisissez une raison qui
                    n'en demande pas.
                  </Trans>
                </p>
              )}

              {reading.hints.clickAgainToRemove && (
                <p className="text-xs text-slate-500">
                  <Trans>
                    Cliquez à nouveau sur cette raison pour la retirer et
                    confirmer la valeur.
                  </Trans>
                </p>
              )}

              {reading.hints.chooseReason && (
                <p className="text-xs text-slate-500">
                  <Trans>
                    Choisissez la raison de l'erreur : l'enregistrement reste
                    indisponible tant qu'aucune n'est sélectionnée.
                  </Trans>
                </p>
              )}

              {/* Ce que l'on sait déjà de cette correction — jamais une raison de ne pas
                  poser la question : la classe est un clic et c'est la matière première
                  du laboratoire. */}
              {context?.hints.reason && (
                <p className="rounded-lg border border-primary/20 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                  {context.hints.reason}
                </p>
              )}

              {needsTargetField && context && (
                <TargetFieldPicker
                  fields={context.sibling_fields}
                  selected={targetField}
                  onSelect={setTargetField}
                />
              )}

              {/* Required on five classes of eight — those whose reason fits neither a
                  target field nor the corrected value — and optional on the other three.

                  Ce qui était ici — « Quel passage l'établit ? » et la liste des passages
                  du registre — a été retiré. Sur 169 corrections classées en production,
                  ce sélecteur a produit ZÉRO citation : il chargeait jusqu'à 40 extraits
                  du dossier à chaque clic sur le crayon pour un contrôle que personne
                  n'utilisait. Ce que la citation portait pour « c'est écrit dans un
                  document » — QUEL document — est désormais demandé en une ligne. */}
              {showsProse && (
                <div className="grid gap-1.5">
                  <Label htmlFor={`${property}-explanation`}>
                    {effectiveErrorClass === "MISSED_IN_SOURCE" ? (
                      <Trans>Dans quel document est-ce écrit ?</Trans>
                    ) : effectiveErrorClass === "MISREAD_SOURCE" ? (
                      <Trans>Que dit réellement le document ?</Trans>
                    ) : effectiveErrorClass === "OVER_EXTRACTED" ? (
                      <Trans>
                        Pourquoi ce texte ne devait pas remplir ce champ ?
                      </Trans>
                    ) : effectiveErrorClass === "MISATTRIBUTED" ? (
                      <Trans>
                        À qui ou à quelle date cela se rapporte-t-il ?
                      </Trans>
                    ) : offersOptionalProse ? (
                      <Trans>Précision (facultatif)</Trans>
                    ) : (
                      <Trans>Sur quoi vous appuyez-vous ?</Trans>
                    )}
                  </Label>
                  <Textarea
                    id={`${property}-explanation`}
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    placeholder={
                      effectiveErrorClass === "MISSED_IN_SOURCE"
                        ? t`Ex. : compte rendu opératoire du 12/03/2024.`
                        : effectiveErrorClass === "OVER_EXTRACTED"
                          ? t`Ex. : le compte rendu dit « pas de tabac », ou l'acte était seulement prévu.`
                          : effectiveErrorClass === "MISATTRIBUTED"
                            ? t`Ex. : il s'agit de l'épisode de 2019, pas de celui-ci.`
                            : effectiveErrorClass === "MISREAD_SOURCE"
                              ? t`Ex. : « discectomie envisagée » ne veut pas dire réalisée.`
                              : offersOptionalProse
                                ? optionalProsePlaceholder
                                : t`Ex. : erreur de dictée, lire « gauche » et non « droite » ; ou antécédent connu, jamais consigné.`
                    }
                    className="min-h-[70px]"
                  />
                  {effectiveErrorClass === "MISSED_IN_SOURCE" && (
                    <p className="text-xs text-slate-500">
                      <Trans>
                        Un type de document et une date suffisent : c'est ce qui
                        permet de corriger la sélection des documents envoyés au
                        modèle.
                      </Trans>
                    </p>
                  )}
                  {effectiveErrorClass === "CLINICAL_JUDGEMENT" && (
                    <p className="text-xs text-slate-500">
                      <Trans>
                        Cette correction sera exclue du score d'extraction :
                        aucune lecture des documents ne pouvait produire cette
                        valeur.
                      </Trans>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Ce que l'enregistrement va écrire, dit en toutes lettres.
            C'est la contrepartie du bouton unique : le libellé ne porte plus le geste,
            donc une ligne doit le porter. Elle vaut mieux que trois libellés, parce
            qu'elle décrit l'état du formulaire au lieu de renommer une action. */}
        {saveError && !confirmingClose && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {saveError}
          </p>
        )}
        {recordsFeedback && !confirmingClose && !saveError && (
          <p className="text-xs text-slate-600">
            {reading.summary === "corrected" ? (
              <Trans>Sera enregistré comme une correction de la valeur.</Trans>
            ) : reading.summary === "revised" ? (
              <Trans>
                Sera enregistré comme une modification de la correction : la
                valeur reste corrigée.
              </Trans>
            ) : reading.summary === "flagged" ? (
              <Trans>
                Sera enregistré comme un signalement : la valeur est fausse,
                sans correction proposée.
              </Trans>
            ) : (
              <Trans>
                Sera enregistré comme une confirmation : la valeur affichée est
                la bonne.
              </Trans>
            )}
          </p>
        )}
        <DialogFooter>
          {confirmingClose ? (
            <div className="flex w-full flex-col gap-3 rounded-lg border border-primary/30 bg-blue-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-blue-900">
                <Trans>
                  Vos modifications ne seront pas enregistrées : le champ
                  restera tel qu'il était.
                </Trans>
              </span>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  className="bg-white"
                  onClick={() => setConfirmingClose(false)}
                >
                  <Trans>Revenir à l'édition</Trans>
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setConfirmingClose(false);
                    setIsOpen(false);
                  }}
                >
                  <Trans>Fermer sans enregistrer</Trans>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={requestClose}>
                <Trans>Annuler</Trans>
              </Button>
              {/* Un seul bouton pour les trois gestes, et un seul libellé. Grisé tant que
                  la justification n'est pas complète ; le titre dit alors ce qui manque —
                  un bouton inerte sans raison est une impasse. */}
              <Button
                onClick={() => giveCorrectionFeedback()}
                disabled={
                  saving ||
                  justificationBlocked ||
                  scoresComputing ||
                  nothingToRevise
                }
                title={
                  scoresComputing
                    ? t`Recalcul du score en cours`
                    : reasonNeedsAValue
                      ? t`Cette raison suppose la bonne valeur : donnez-la, ou choisissez-en une autre`
                      : justificationBlocked && !effectiveErrorClass
                        ? t`Sélectionnez d'abord la raison de l'erreur`
                        : justificationBlocked
                          ? t`Complétez la justification demandée par la raison choisie`
                          : nothingToRevise
                            ? t`Modifiez la valeur, la raison ou le texte pour enregistrer`
                            : undefined
                }
              >
                {saving ? (
                  <Trans>Enregistrement en cours...</Trans>
                ) : (
                  <Trans>Enregistrer</Trans>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
